import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Crest } from '@/components/Brand';
import { toast } from 'sonner';

const RULES = [
  { test: (p) => p.length >= 8, label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
  { test: (p) => /\d/.test(p), label: 'One number' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'One special character' },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);

  const allOk = RULES.every(r => r.test(pw)) && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!allOk) return;
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      toast.success('Password updated. Please log in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset password');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-trjlight flex items-center justify-center p-6" data-testid="reset-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-md w-full">
        <Crest size="lg" className="mx-auto" />
        <h1 className="font-display font-semibold text-2xl text-navy mt-4 text-center">Set a new password</h1>
        {!token ? (
          <p className="mt-6 text-sm text-red-600 text-center">Missing reset token. Please use the link from your email.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>New password</Label>
              <Input type="password" data-testid="reset-pw1" value={pw} onChange={(e) => setPw(e.target.value)} required /></div>
            <div><Label>Confirm new password</Label>
              <Input type="password" data-testid="reset-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} required /></div>
            <ul className="text-xs space-y-1 text-slate-600 bg-trjlight rounded p-3">
              {RULES.map((r, i) => (
                <li key={i} className={r.test(pw) ? 'text-trjgreen' : ''}>{r.test(pw) ? '✓' : '○'} {r.label}</li>
              ))}
              <li className={pw && pw === pw2 ? 'text-trjgreen' : ''}>{pw && pw === pw2 ? '✓' : '○'} Passwords match</li>
            </ul>
            <Button type="submit" className="btn-navy w-full h-12" disabled={!allOk || busy} data-testid="reset-submit">
              {busy ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        )}
        <p className="text-sm text-slate-500 text-center mt-6">
          <Link to="/login" className="text-navy font-semibold hover:text-gold">Back to log in</Link>
        </p>
      </div>
    </div>
  );
}
